import * as React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { styled } from "@mui/material";
import { KAHUN_PLATFORM } from "@kahunmedical/user-management";
import { PatientCardCommonActions } from "./patientCardSummaryOrChartToggle";
import { ThemeSettings } from "@kahunmedical/common-configuration";
import { PatientCardTopAction } from "./patientCardCommon";
import {
  ConfigurationTab,
  KahunSliderTabs,
} from "../settings/presentaional/kahunSliderTabs";
import { PatientCardAgeAndGender } from "./patientCardAgeAndGender";
import { usePatientSummary } from "fe-common/patientSummary/useFetchPatientSummary";
import { ThreeDotsLoader } from "../loader/threeDotsLoader";
import { PatientCardSectionTemplate } from "./layout/patientCardSectionTemplate";
import { useTranslateByGender } from "fe-common/locale/useTranslateByGender";
import { CommonPatientCardDd } from "./commonPatientCardDd";
import {
  ConversationOptions,
  SortType,
  SummaryOptions,
} from "fe-common/graphql";
import { CommonColors } from "../colors/commonColors";
import { GroupedWorkupList } from "./groupedWorkupList";
import { PatientSummaryCommonWrapper } from "./patientSummaryCommonWrapper";
import { PatientSummaryAction } from "./types/SectionAction";
import { PatientCardLLMText } from "./patientCardLLMText";
import Toast from "../layout/toast";
import { GroundedDataParagraph } from "../grounding/grounding";
import { ClinicConfigKeys } from "fe-common/graphql/clinic/clinicConfigKeys";
import clsx from "clsx";
import { useClinicState } from "ask/src/clinic/clinicStateContext";

//region [[ Styles ]]
const TabbedPatientCardView = styled("div")({
  "&.rtl": {
    direction: "rtl",
  },
});
const SectionTabs = styled(KahunSliderTabs)({
  "& .top-bar-area": {
    minHeight: "50px",
    marginBottom: 0,
    background: "white",
    "& > div": {
      width: "100%",
    },
    "& button": {
      flex: 1,
      margin: "0",
      //height: "100%",
      justifyItems: "stretch",
      borderRadius: "0",
      padding: "14px 0px",
      height: "fit-content",
      fontSize: "12.5px",
      fontWeight: 700,
      color: "rgba(36, 52, 67, 0.45)",
      "&.tab-selected": {
        color: CommonColors.Default,
      },
    },
    "& .divider:not(:last-of-type)": {
      borderRight: "1px solid lightgrey",
      display: "flex",
      position: "static",
      height: "30px",
    },
  },
  "& .column-header": {
    display: "flex",
    flexFlow: "row",
    justifyContent: "flex-end",
  },
});
//endregion [[ Styles ]]

//region [[ Props ]]
export interface TabbedPatientCardProps {
  className?: string;
  caseId: string;
  clinicId: string;
  clinicName?: string;
  actions?: PatientCardCommonActions[];
  themeSettings: ThemeSettings;
  topActions?: PatientCardTopAction[];
  platform?: KAHUN_PLATFORM;
  supportedSections: string[];
  inExtension?: boolean;
  summarySectionActions?: PatientSummaryAction[];
  onLoading?: (loading: boolean) => void;
  summaryOptions?: SummaryOptions;
  defaultLocale?: string;
}

//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]

export const TabbedPatientCard = ({
  caseId,
  clinicId,
  clinicName,
  topActions,
  platform = KAHUN_PLATFORM.UNKNOWN,
  defaultLocale,
  ...props
}: TabbedPatientCardProps) => {
  const { locale: locale_, translate } = useTranslateByGender();
  const locale = defaultLocale || locale_;
  const [toasterLabel, setToasterLabel] = useState(null);
  const [{ clinicConfig }] = useClinicState();
  const preLoadingState = useRef(false);

  const patientSummary = usePatientSummary(
    caseId,
    clinicId,
    null,
    true,
    clinicConfig?.[ClinicConfigKeys.UsesLLM]?.value,
    clinicConfig?.[ClinicConfigKeys.GenerateInsightsFromClient], //use Dialogue
    clinicConfig?.[ClinicConfigKeys.GroundAssessment]?.value,
    clinicConfig?.[ClinicConfigKeys.GroundExplanation]?.value,
    props.summaryOptions,
  );
  //In Hebrew also use DD in English
  const patientSummaryEn = usePatientSummary(
    locale === "he" ? caseId : null,
    clinicId,
    "en",
  );
  const dd =
    patientSummaryEn?.possibleCauses?.possibleCauses ||
    patientSummary?.possibleCauses?.possibleCauses ||
    patientSummaryEn?.possibleCause ||
    patientSummary?.possibleCause;

  useEffect(() => {
    if (!patientSummary.loading && preLoadingState.current === true) {
      // Only fire if it had changed from true to false (initiali both would be false)
      props?.onLoading?.(true);
    } else props?.onLoading?.(false);
    preLoadingState.current = patientSummary.loading;
  }, [patientSummary.loading]);

  const handleTopActionClicked = (
    index: number,
    e: React.MouseEvent<any>,
    section: string,
  ) => {
    topActions?.[index].onClick?.(e, patientSummary);
  };

  const tabs: ConfigurationTab[] = [
    {
      name: "Summary",
      key: "summary",
      tooltip: translate("SUMMARY_TITLE"),
      component: (
        <PatientCardSectionTemplate
          title={
            <>
              <span>{translate("SUMMARY_TITLE")}</span>
              {topActions?.map((act, i) =>
                React.cloneElement(act.element as any, {
                  key: i,
                  onClick: (e) => handleTopActionClicked(i, e, "summary"),
                }),
              )}
            </>
          }
          content={
            <PatientSummaryCommonWrapper
              inExtension={props.inExtension}
              patientSummary={patientSummary}
              caseId={caseId}
              clinicName={clinicName}
              clinicId={clinicId}
              supportedSections={props.supportedSections}
              actions={props.actions}
              platform={platform}
              sectionActions={props.summarySectionActions}
              onNotify={setToasterLabel}
              hideTitle
            >
              {patientSummary?.summary && (
                <PatientCardAgeAndGender
                  themeSettings={props.themeSettings}
                  summary={patientSummary?.summary}
                />
              )}
            </PatientSummaryCommonWrapper>
          }
        />
      ),
    },
    {
      name: translate("POSSIBLE_CAUSES"),
      key: "pc",
      tooltip: translate("POSSIBLE_CAUSES"),
      component: (
        <PatientCardSectionTemplate
          className={"patient-card-dd"}
          title={
            <>
              {translate("POSSIBLE_CAUSES")}
              {topActions?.map((act, i) =>
                React.cloneElement(act.element as any, {
                  key: i,
                  onClick: (e) => handleTopActionClicked(i, e, "summary"),
                }),
              )}
            </>
          }
          content={
            <CommonPatientCardDd
              sortType={SortType.PatientProb}
              dd={dd}
              inExtension={props.inExtension}
            />
          }
        />
      ),
    },
    !patientSummary?.assessment?.text &&
    !patientSummary?.assessment?.grounding?.grounding
      ? null
      : {
          name: translate("ASSESSMENT"),
          key: "ASSESSMENT",
          tooltip: translate("ASSESSMENT"),
          component: (
            <PatientCardSectionTemplate
              className={"patient-card-dd"}
              title={
                <>
                  {translate("ASSESSMENT")}
                  {topActions?.map((act, i) =>
                    React.cloneElement(act.element as any, {
                      key: i,
                      onClick: (e) => handleTopActionClicked(i, e, "summary"),
                    }),
                  )}
                </>
              }
              content={
                <PatientCardLLMText
                  onShowMessage={setToasterLabel}
                  inExtension={props.inExtension}
                  actions={props.summarySectionActions}
                  summaryApi={patientSummary}
                  clinicId={clinicId}
                  clinicName={clinicName}
                  platform={KAHUN_PLATFORM.Extension}
                  sectionName={"assessment"}
                >
                  {patientSummary?.assessment?.grounding?.grounding ? (
                    <GroundedDataParagraph
                      groundedData={
                        patientSummary?.assessment?.grounding?.grounding
                      }
                      outputReferenceSection={false}
                    />
                  ) : (
                    patientSummary?.assessment?.text
                  )}
                </PatientCardLLMText>
              }
            />
          ),
        },

    !patientSummary?.workupActionItems?.items?.length ||
    patientSummary?.workup?.text
      ? null
      : {
          name: translate("SUGGESTED_WORKUP"),
          key: "workup",
          tooltip: translate("SUGGESTED_WORKUP"),
          component: (
            <PatientCardSectionTemplate
              className={"patient-card-dd"}
              title={translate("SUGGESTED_WORKUP")}
              content={
                <PatientCardLLMText
                  onShowMessage={setToasterLabel}
                  inExtension={props.inExtension}
                  actions={props.summarySectionActions}
                  clinicId={clinicId}
                  clinicName={clinicName}
                  platform={KAHUN_PLATFORM.Extension}
                  sectionName={"workup"}
                >
                  {(!!patientSummary?.workupActionItems?.items && (
                    <GroupedWorkupList
                      workupList={patientSummary?.workupActionItems?.items}
                    />
                  )) ||
                    (!patientSummary.polling?.workup &&
                      patientSummary?.workup?.text)}
                </PatientCardLLMText>
              }
            />
          ),
        },
  ];

  if (patientSummary.loading) return <ThreeDotsLoader />;

  return (
    <TabbedPatientCardView className={clsx(locale === "he" ? "rtl" : "")}>
      <SectionTabs
        selectedIndex={0}
        tabs={tabs}
        inExtension={props.inExtension}
      />
      <Toast
        position={"absolute"}
        text={toasterLabel}
        open={!!toasterLabel}
        onClose={() => setToasterLabel(null)}
      />
    </TabbedPatientCardView>
  );
};
